import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from 'react-query';

import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import { pauseSubscription } from '../api';
import { useGlobalContext } from '../state/GlobalContext';
import { startMomentsApplicationsRefetchBackoff } from '../../utils/momentsRefetchBackoff';

const ConfirmPauseModal: React.FC = () => {
  const { t } = useTranslation();
  const { customerPortalSettings, shopHasActiveMoments } = useGlobalContext();
  const handleOnClose = () => {
    onClose();
  };
  const { goToFirstStep, onClose } = useModalManagerContext();
  const { customerId, subscriptionId, authToken } = useGlobalContext();
  const queryClient = useQueryClient();

  const isRescheduleHidden =
    customerPortalSettings?.hideReschedule ||
    customerPortalSettings?.hideEditNextOrderDate;

  const { isLoading, mutate } = useMutation(
    () => pauseSubscription(customerId, subscriptionId, authToken),

    {
      onSuccess: async (data) => {
        const previousValue = queryClient.getQueryData<Subscription>([
          'subscription',
          customerId,
          subscriptionId,
        ]);

        onClose(t('subscription.pause_success'));

        queryClient.setQueryData(['subscription', customerId, subscriptionId], {
          ...previousValue,
          SubscriptionStatus: data.SubscriptionStatus,
        });
        if (shopHasActiveMoments) {
          startMomentsApplicationsRefetchBackoff({
            queryClient,
            subscriptionId,
          });
        }
      },
      // On failure, roll back to the previous value
      onError: (err, variables, previousValue) => {
        onClose(t('subscription.pause_error'), { type: 'error' });
        console.error(err);
      },
    },
  );

  const handleOnConfirmClick = () => {
    mutate();
  };

  return (
    <>
      <ModalHeader>{t('pauseModal.pauseModalTitle')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Box>
          <Text>{t('pauseModal.pauseModalBody')}</Text>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button
          mr="4"
          onClick={isRescheduleHidden ? handleOnClose : goToFirstStep}
          variant="outlinedBig"
          isLoading={isLoading}
          disabled={isLoading}
        >
          {isRescheduleHidden
            ? t('pauseModal.neverMind')
            : t('pauseModal.back')}
        </Button>
        <Button
          onClick={handleOnConfirmClick}
          variant="blackBig"
          disabled={isLoading}
          isLoading={isLoading}
        >
          {t('pauseModal.confirmPause')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ConfirmPauseModal;
